import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { ColumnId } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { createStyles } from '@/contexts/theme-context';
import {
  ESTIMATED_HEADER_HEIGHT,
  ESTIMATED_ROW_HEIGHT,
} from './order-entry-table-constants';
import { filterAndRankOrderEntryRows } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/utils/filter-and-rank-order-entry-rows';
import { LayoutChangeEvent, View, ViewStyle } from 'react-native';
import { OnChangeFn, SortingState, TableState } from '@tanstack/react-table';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { type OrderEntryTableMeta } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import {
  OrderEntryTableRowData,
  RequestNoteSavePayload,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { passThroughFilter } from '@/experiences/sales-portal/blocs/utils/fuzzy-search';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { SalesPortalKeyboardScope } from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { Table } from '@experiences/components/table/table';
import { useOrderEntryTableConfig } from './use-order-entry-table-config';
import { useOrderEntryTableRendering } from './use-order-entry-table-rendering';

import type { LastPurchaseInfoMap } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/fulfilled-date-transformer';
// Multiplier for the bottom visibility buffer (in row heights).
const ROW_VISIBILITY_BUFFER_ROWS_MULTIPLIER = 2;

interface OrderEntryTableProps {
  areSubstitutionsEnabled?: boolean;
  bodyContainerStyle?: ViewStyle;
  canSetManualOverrideExpiration?: boolean;
  canWriteRequestNote?: boolean;
  containerStyle?: ViewStyle;
  data: OrderEntryTableRowData[];
  emptyState: React.ReactNode;
  initialSorting?: SortingState;
  isActive?: boolean;
  isGrossProfitHidden?: boolean;
  isPriceEditingEnabled?: boolean;
  lastPurchaseInfoMap?: LastPurchaseInfoMap;
  listFooter?: React.ReactNode;
  minMaxQuantityEnabled?: boolean;
  onFocusedRowChange?: (rowData: OrderEntryTableRowData | null) => void;
  onOpenRestaurantSelector?: () => void;
  onPriceValidationError: (message: string) => void;
  /**
   * Invoked when a row's background is pressed (cell-level inputs handle
   * their own touches). Receives the row's data so consumers can decide
   * what to do — e.g. opening the item detail side panel.
   */
  onRowPress?: (rowData: OrderEntryTableRowData) => void;
  onSaveCart: () => void;
  onSavePriceEdit: (editor: PriceEditor, variantPackUUID: string) => void;
  onSaveRequestNote?: (payload: RequestNoteSavePayload) => Promise<void>;
  onSwitchBuildReviewMode?: () => void;
  onToggleCheckout?: () => void;
  onTriggerSearch?: () => void;
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  quantityInputDisabled?: boolean;
  selectedCollectionId?: string;
  showPriceOverrideInfo?: boolean;
  tableState?: Partial<TableState>;
}

const OrderEntryTableComponent: React.FC<OrderEntryTableProps> = ({
  areSubstitutionsEnabled,
  bodyContainerStyle,
  canSetManualOverrideExpiration,
  containerStyle,
  data,
  emptyState,
  initialSorting,
  isActive = true,
  isGrossProfitHidden,
  canWriteRequestNote,
  isPriceEditingEnabled,
  lastPurchaseInfoMap,
  listFooter,
  minMaxQuantityEnabled,
  onFocusedRowChange,
  onOpenRestaurantSelector,
  onPriceValidationError,
  onRowPress,
  onSaveCart,
  onSavePriceEdit,
  onSaveRequestNote,
  onSwitchBuildReviewMode,
  onToggleCheckout,
  onTriggerSearch,
  priceEditDisplayHeaders,
  quantityInputDisabled,
  selectedCollectionId,
  showPriceOverrideInfo,
  tableState,
}) => {
  const [styles, theme] = useStyles();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);

  // PERF: Use transition to make sorting non-blocking
  const [, startTransition] = useTransition();
  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (newSorting) => {
      // PERF: Wrap in startTransition to allow browser to paint immediately
      startTransition(() => {
        setSorting(newSorting);
      });
    },
    [startTransition],
  );

  const editableColumns = useMemo(
    () =>
      isPriceEditingEnabled
        ? [ColumnId.Quantity, ColumnId.Price, ColumnId.GpPercentage]
        : [ColumnId.Quantity],
    [isPriceEditingEnabled],
  );

  const { columnOrder, columnVisibility, columns, pinnedColumns } =
    useOrderEntryTableConfig({
      areSubstitutionsEnabled,
      canSetManualOverrideExpiration,
      canWriteRequestNote,
      data,
      isGrossProfitHidden,
      isPriceEditingEnabled,
      lastPurchaseInfoMap,
      minMaxQuantityEnabled,
      onOpenRestaurantSelector,
      onPriceValidationError,
      onSaveCart,
      onSavePriceEdit,
      onSaveRequestNote,
      onSwitchBuildReviewMode,
      onToggleCheckout,
      onTriggerSearch,
      priceEditDisplayHeaders,
      quantityInputDisabled,
      selectedCollectionId,
      showPriceOverrideInfo,
    });
  const { renderHeaderRow, renderRow } = useOrderEntryTableRendering({
    onRowPress,
  });

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setLayoutWidth(width);
  }, []);

  const meta = useMemo(
    (): OrderEntryTableMeta => ({
      areSubstitutionsEnabled: !!areSubstitutionsEnabled,
      isPriceEditingEnabled: !!isPriceEditingEnabled,
    }),
    [areSubstitutionsEnabled, isPriceEditingEnabled],
  );

  const deferredFilter = useDeferredValue(
    tableState?.globalFilter ? String(tableState.globalFilter) : '',
  );

  const filteredData = useMemo(
    () => filterAndRankOrderEntryRows(data, deferredFilter),
    [data, deferredFilter],
  );

  const memoizedTableState = useMemo((): Partial<TableState> => {
    return {
      columnOrder,
      columnPinning: {
        left: [ColumnId.Quantity, ColumnId.Name],
      },
      columnVisibility,
      sorting,
      ...tableState,
    };
  }, [columnOrder, columnVisibility, sorting, tableState]);

  // Show loading state until we have layout width
  if (!layoutWidth) {
    return (
      <View
        accessibilityLabel={i18n.t('sales_portal.order_entry.table.title')}
        accessibilityRole="progressbar"
        accessibilityState={{ busy: true, expanded: false }}
        accessible
        onLayout={handleLayout}
      />
    );
  }

  return (
    <View
      accessibilityLabel={i18n.t('sales_portal.order_entry.table.title')}
      onLayout={handleLayout}
      style={[styles.container, containerStyle]}
    >
      <Table
        bodyContainerStyle={bodyContainerStyle}
        cellPadding={[
          [theme.sk.spacing.spaceSmall, 0],
          ...Array(columns.length - 1).fill([
            theme.sk.spacing.spaceMedium,
            theme.sk.spacing.spaceMedium,
          ]),
        ]}
        columns={columns}
        data={filteredData}
        editableColumns={editableColumns}
        emptyComponent={emptyState}
        estimatedHeaderHeight={ESTIMATED_HEADER_HEIGHT}
        estimatedRowHeight={ESTIMATED_ROW_HEIGHT}
        globalFilterFn={passThroughFilter}
        gutters={[0, theme.sk.spacing.spaceLarge]}
        initialState={{
          columnPinning: pinnedColumns,
        }}
        isActive={isActive}
        keyboardShortcutScope={SalesPortalKeyboardScope.ORDER_ENTRY_TABLE}
        layoutWidth={layoutWidth}
        listFooter={listFooter}
        meta={meta}
        onFocusedRowChange={onFocusedRowChange}
        onSortingChange={onSortingChange}
        renderHeaderRow={renderHeaderRow}
        renderRow={renderRow}
        rowVisibilityBottomBufferMultiplier={
          ROW_VISIBILITY_BUFFER_ROWS_MULTIPLIER
        }
        state={memoizedTableState}
      />
    </View>
  );
};

export const OrderEntryTable = React.memo(OrderEntryTableComponent);

const useStyles = createStyles(() => ({
  container: {
    flex: 1,
  },
}));
